import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronRight, ChevronLeft, CreditCard, MapPin, Phone, Truck } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getDPDParcelMachines } from '../services/dpd';
import { fetchSmartpostLocations } from '../services/smartpost';
import { fetchOmnivaLocations } from '../services/omniva';
import { DPDParcelMachine, SmartpostParcelMachine, OmnivaParcelMachine } from '../types/shipping';
import { createEstoPayment } from '../services/payments';
import { useCart } from '../context/CartContext';
import { getNextOrderId, formatOrderId } from '../utils/orderUtils';
import { CheckoutProgress } from '../components/checkout/CheckoutProgress';
import { CheckoutContactForm } from '../components/checkout/CheckoutContactForm';
import { CheckoutShippingMethod } from '../components/checkout/CheckoutShippingMethod';
import { CheckoutAddressForm } from '../components/checkout/CheckoutAddressForm';
import { CheckoutPaymentMethod } from '../components/checkout/CheckoutPaymentMethod';
import { CheckoutReview } from '../components/checkout/CheckoutReview';
import { CheckoutConfirmation } from '../components/checkout/CheckoutConfirmation';

type Step = 'contact' | 'shipping' | 'address' | 'payment' | 'review' | 'confirmation';


interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  shippingMethod: string;
  address?: {
    street: string;
    city: string;
    postalCode: string;
  };
  paymentMethod: string;
  agreedToTerms: boolean;
  agreedToPrivacy: boolean;
  marketingConsent: boolean;
  errors?: Record<string, string>;
}

const steps: { id: Step; title: string; icon: React.ReactNode }[] = [
  { id: 'contact', title: 'Kontakt', icon: <Phone className="w-5 h-5" /> },
  { id: 'shipping', title: 'Transport', icon: <Truck className="w-5 h-5" /> },
  { id: 'address', title: 'Aadress', icon: <MapPin className="w-5 h-5" /> },
  { id: 'payment', title: 'Maksmine', icon: <CreditCard className="w-5 h-5" /> },
  { id: 'review', title: 'Ülevaade', icon: <Check className="w-5 h-5" /> },
  { id: 'confirmation', title: 'Kinnitus', icon: <Check className="w-5 h-5" /> },
];

const CHECKOUT_STORAGE_KEY = 'checkout_form_data';

function Checkout() {
  const navigate = useNavigate();
  const { items, clearCart, appliedPromocode } = useCart();
  const [currentStep, setCurrentStep] = useState<Step>('contact');
  const [dpdParcelMachines, setDpdParcelMachines] = useState<DPDParcelMachine[]>([]);
  const [smartpostParcelMachines, setSmartpostParcelMachines] = useState<SmartpostParcelMachine[]>([]);
  const [omnivaParcelMachines, setOmnivaParcelMachines] = useState<OmnivaParcelMachine[]>([]);
  const [parcelMachineSearch, setParcelMachineSearch] = useState('');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    shippingMethod: '',
    paymentMethod: '',
    agreedToTerms: false,
    agreedToPrivacy: false,
    marketingConsent: false,
    errors: {}
  });

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
      errors: {
        ...prev.errors,
        [name]: '' // Clear any previous error for this checkbox
      }
    }));
  };

  // Load saved form data from sessionStorage on component mount
  useEffect(() => {
    async function fetchAllParcelMachines() {
      if (formData.shippingMethod === 'DPD Pakiautomaat') {
        const machines = await getDPDParcelMachines();
        setDpdParcelMachines(machines);
        setSmartpostParcelMachines([]);
        setOmnivaParcelMachines([]);
      } else if (formData.shippingMethod === 'Smartposti Pakiautomaat') {
        const machines = await fetchSmartpostLocations();
        setSmartpostParcelMachines(machines);
        setDpdParcelMachines([]);
        setOmnivaParcelMachines([]);
      } else if (formData.shippingMethod === 'Omniva Pakiautomaat') {
        const machines = await fetchOmnivaLocations();
        setOmnivaParcelMachines(machines);
        setDpdParcelMachines([]);
        setSmartpostParcelMachines([]);
      } else {
        setDpdParcelMachines([]);
        setSmartpostParcelMachines([]);
        setOmnivaParcelMachines([]);
      }
    }
    fetchAllParcelMachines();
  }, [formData.shippingMethod]);

  // Save form data to sessionStorage when it changes
  useEffect(() => {
    try {
      const dataToSave = { ...formData };
      delete dataToSave.errors; // Don't save error state
      sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving checkout data:', error);
    }
  }, [formData]);

  // Calculate order totals
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shipping = 0; // Free shipping always
  const promocodeDiscount = appliedPromocode ? appliedPromocode.discountAmount : 0;
  const total = subtotal + shipping - promocodeDiscount;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      errors: {
        ...prev.errors,
        [name]: ''
      }
    }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateContactForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.firstName.trim()) errors.firstName = 'Eesnimi on kohustuslik';
    if (!formData.lastName.trim()) errors.lastName = 'Perenimi on kohustuslik';
    if (!formData.email.trim()) errors.email = 'E-post on kohustuslik';
    else if (!validateEmail(formData.email.trim())) errors.email = 'Sisesta korrektne e-posti aadress';
    if (!formData.phone.trim()) errors.phone = 'Telefon on kohustuslik';
    
    setFormData(prev => ({ ...prev, errors }));
    return Object.keys(errors).length === 0;
  };

  const handleShippingMethodChange = (method: string) => {
    setFormData(prev => ({ ...prev, shippingMethod: method }));
    
    // Go to address step for all shipping methods (since "Tulen ise järgi" is commented out)
    setCurrentStep('address');
  };

  const handlePaymentMethodChange = (method: string) => {
    setFormData(prev => ({ ...prev, paymentMethod: method }));
    
    // Only automatically proceed if the user is selecting a different method
    // or if they're selecting it for the first time
    if (method !== formData.paymentMethod) {
      handleNext();
    }
  };

  const getVisibleSteps = () => {
    return steps.filter(step => 
      // !(step.id === 'address' && formData.shippingMethod === 'Tulen ise järgi') && // Commented out since "Tulen ise järgi" is hidden
      !(step.id === 'confirmation' && !orderId)
    );
  };

  const handleNext = () => {
    if (currentStep === 'contact' && !validateContactForm()) {
      return;
    }

    const visibleSteps = getVisibleSteps();
    const currentIndex = visibleSteps.findIndex(step => step.id === currentStep);
    if (currentIndex < visibleSteps.length - 1) {
      setCurrentStep(visibleSteps[currentIndex + 1].id);
    }
  };

  const handleBack = () => {
    const visibleSteps = getVisibleSteps();
    const currentIndex = visibleSteps.findIndex(step => step.id === currentStep);
    
    if (currentIndex > 0) {
      const previousStep = visibleSteps[currentIndex - 1].id;
      
      // No special handling needed since "Tulen ise järgi" is commented out
      setCurrentStep(previousStep);
    } else {
      navigate('/ostukorv');
    }
  };

  const getShippingAddress = () => {
    // No special handling needed since "Tulen ise järgi" is commented out
    return formData.address 
      ? `${formData.address.street}, ${formData.address.city}, ${formData.address.postalCode}` 
      : undefined;
  };

  const processOrder = async () => {
    if (currentStep !== 'review') return;

    const errors: Record<string, string> = {};
    if (!formData.agreedToTerms) {
      errors.agreedToTerms = 'Palun nõustu müügitingimustega.';
    }
    if (!formData.agreedToPrivacy) {
      errors.agreedToPrivacy = 'Palun nõustu privaatsuspoliitikaga.';
    }
  
    if (Object.keys(errors).length > 0) {
      setFormData(prev => ({ ...prev, errors }));
      return; // Stop execution if there are errors
    }
    
    setIsProcessing(true);
    
    try {
      // Add short loading delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Generate a unique order ID
      const formattedOrderId = await getNextOrderId();
      setOrderId(formattedOrderId);
      
      // Prepare order data
      const lineItems = items.map(item => ({
        name: item.name,
        unitPrice: item.price,
        quantity: item.quantity,
      }));

      // Determine payment method key and selected payment type
      let paymentMethodKey = "BANK_LINK";
      let selectedPaymentType = "esto";
      
      if (formData.paymentMethod === 'ESTO järelmaks') {
        paymentMethodKey = "ESTO_INSTALLMENT";
        selectedPaymentType = "esto_jarelmaks";
      }
      const paymentData = {
        amount: total,
        merchantReference: formattedOrderId,
        returnUrl: `${window.location.origin}/tellimus-esitatud`,
        customerFirstName: formData.firstName,
        customerLastName: formData.lastName,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        customerAddress: getShippingAddress(),
        shippingMethod: formData.shippingMethod,
        lineItems: lineItems,
        payment_method_key: paymentMethodKey,
        marketingConsent: formData.marketingConsent,
      };

      // Store order data in sessionStorage for the success page to use for email sending
      const orderDataForEmail = {
        orderId: formattedOrderId,
        email: formData.email,
        customerName: `${formData.firstName} ${formData.lastName}`,
        orderTotal: total,
        paymentMethod: formData.paymentMethod,
        shippingMethod: formData.shippingMethod,
        shippingAddress: getShippingAddress(),
        appliedPromocode: appliedPromocode,
        marketingConsent: formData.marketingConsent,
        items: items.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          image: item.image
        }))
      };
      sessionStorage.setItem('orderDataForEmail', JSON.stringify(orderDataForEmail));

      if (formData.paymentMethod === 'Pangamakse (Swedbank, SEB, LHV, ja teised)' || 
          formData.paymentMethod === 'ESTO järelmaks') {
        try {
          console.log(`Creating ESTO payment (${selectedPaymentType}) with data:`, {
            ...paymentData,
            customerPhone: '****' // Don't log the full phone number
          });
          
          // Process with ESTO payment gateway
          const response = await createEstoPayment(paymentData, false, selectedPaymentType);
          
          if (response && response.paymentUrl) {
            // Clear checkout form data from sessionStorage after successful order
            sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
            
            // Clear cart before redirecting
            clearCart();
            
            // Redirect to ESTO payment page
            window.location.href = response.paymentUrl;
            return; // Important: stop execution here as we're redirecting
          } else {
            throw new Error('Payment URL not received from payment processor');
          }
        } catch (error) {
          console.error('Error creating ESTO payment:', error);
          throw error;
        }
      } else {
        // Regular bank transfer
        // Clear checkout form data from sessionStorage after successful order
        sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
        
        // Clear the cart
        clearCart();
        
        // Navigate to success page with order details
        navigate('/tellimus-esitatud', {
          state: {
            orderData: orderDataForEmail
          }
        });
      }
    } catch (error) {
      console.error('Error processing order:', error);
      alert(`Viga tellimuse töötlemisel: ${error instanceof Error ? error.message : 'Tundmatu viga'}`);
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="container mx-auto py-12 px-4 md:px-6">
      {/* Progress Steps */}
      <CheckoutProgress
        currentStep={currentStep}
        steps={steps}
        formData={formData}
        orderId={orderId}
      />

      {/* Form Content */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {currentStep === 'contact' && (
            <CheckoutContactForm 
              formData={formData}
              handleInputChange={handleInputChange}
            />
          )}

          {currentStep === 'shipping' && (
            <CheckoutShippingMethod
              handleShippingMethodChange={handleShippingMethodChange}
              selectedMethod={formData.shippingMethod}
            />
          )}

          {currentStep === 'address' && (
            <CheckoutAddressForm
              formData={formData}
              setFormData={setFormData}
              shippingMethod={formData.shippingMethod}
              dpdParcelMachines={dpdParcelMachines}
              smartpostParcelMachines={smartpostParcelMachines}
              omnivaParcelMachines={omnivaParcelMachines}
              parcelMachineSearch={parcelMachineSearch}
              setParcelMachineSearch={setParcelMachineSearch}
            />
          )}

          {currentStep === 'payment' && (
            <CheckoutPaymentMethod
              handlePaymentMethodChange={handlePaymentMethodChange}
              selectedMethod={formData.paymentMethod}
            />
          )}

          {currentStep === 'review' && (
            <>
              <CheckoutReview 
                formData={formData}
                items={items}
                subtotal={subtotal}
                shipping={shipping}
                total={total}
                appliedPromocode={appliedPromocode}
              />
              <div className="mt-6 space-y-3">
                <div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="agreedToTerms"
                      checked={formData.agreedToTerms}
                      onChange={handleCheckboxChange}
                      className="form-checkbox h-5 w-5 text-[#4FC1E9] rounded focus:ring-[#4FC1E9]"
                    />
                    <span className="ml-2 text-gray-700 text-sm">
                      Nõustun <Link to="/muugitingimused" target="_blank" rel="noopener noreferrer" className="text-[#4FC1E9] hover:underline">müügitingimustega</Link>
                    </span>
                  </label>
                  {formData.errors?.agreedToTerms && (
                    <p className="text-red-500 text-xs mt-1">{formData.errors.agreedToTerms}</p>
                  )}
                </div>
                <div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="agreedToPrivacy"
                      checked={formData.agreedToPrivacy}
                      onChange={handleCheckboxChange}
                      className="form-checkbox h-5 w-5 text-[#4FC1E9] rounded focus:ring-[#4FC1E9]"
                    />
                    <span className="ml-2 text-gray-700 text-sm">
                      Nõustun <Link to="/privaatsustingimused" target="_blank" rel="noopener noreferrer" className="text-[#4FC1E9] hover:underline">privaatsuspoliitikaga</Link>
                    </span>
                  </label>
                  {formData.errors?.agreedToPrivacy && (
                    <p className="text-red-500 text-xs mt-1">{formData.errors.agreedToPrivacy}</p>
                  )}
                </div>
                <div>
                  <label className="inline-flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      name="marketingConsent"
                      checked={formData.marketingConsent}
                      onChange={handleCheckboxChange}
                      className="form-checkbox h-5 w-5 mt-0.5 text-[#4FC1E9] rounded focus:ring-[#4FC1E9] flex-shrink-0"
                    />
                    <span className="ml-2 text-gray-700 text-sm">
                      Soovin tasuta sooduspakkumisi ja promokoode meilile
                    </span>
                  </label>
                </div>
              </div>
            </>
          )}

          {currentStep === 'confirmation' && (
            <CheckoutConfirmation
              orderId={orderId || ""}
              email={formData.email}
            />
          )}
          
          {currentStep !== 'confirmation' && (
            <div className="mt-8 flex justify-between">
              <button
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-[#4FC1E9]"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                {currentStep === 'contact' ? 'Tagasi ostukorvi' : 'Tagasi'}
              </button>
              
              {currentStep === 'contact' && (
                <button
                  onClick={() => validateContactForm() && handleNext()}
                  className="bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white px-6 py-3 rounded-full flex items-center"
                >
                  Järgmine
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
              )}
              
              {currentStep === 'shipping' && formData.shippingMethod && (
                <button
                  onClick={handleNext}
                  className="bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white px-6 py-3 rounded-full flex items-center"
                >
                  Järgmine
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
              )}
              
              {currentStep === 'address' && (
                <button
                  onClick={handleNext}
                  className="bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white px-6 py-3 rounded-full flex items-center"
                >
                  Järgmine
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
              )}
              
              {currentStep === 'payment' && formData.paymentMethod && (
                <button
                  onClick={handleNext}
                  className="bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white px-6 py-3 rounded-full flex items-center"
                >
                  Järgmine
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
              )}
              
              {currentStep === 'review' && (
                <button
                  onClick={processOrder}
                  disabled={isProcessing || !formData.agreedToTerms || !formData.agreedToPrivacy}
                  className="bg-[#4FC1E9] hover:bg-[#3ba8d0] disabled:bg-gray-400 text-white px-6 py-3 rounded-full flex items-center"
                >
                  {isProcessing ? 'Töötleb...' : 'Kinnita tellimus'}
                  {!isProcessing && <ChevronRight className="w-5 h-5 ml-2" />}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default Checkout;
